import { useEffect, useState } from 'react'
import { Activity, FileSignature, Gauge, KeyRound, Loader2, MapPin, Plug, Save, Search, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { NumberInput } from '@/components/ui/NumberInput'
import { Badge } from '@/components/ui/Badge'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import { MedicarSettingsService as Service, type MedicarSettings } from '@/services/modules/MedicarService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'

const onlyDigits = (v: string) => v.replace(/\D+/g, '')
/** Mesmo padrão de CEP dos demais cadastros: 00000-000. */
const maskCep = (v: string) => onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')

/**
 * Configurações → Integrações → Medicar.
 *
 * Usuário e senha da API da Medicar mudam pelo lado deles; quem opera precisa
 * trocá-los sem acesso ao servidor. A senha entra mas não volta — a API
 * devolve só um rastro, e deixar o campo em branco significa "não mexi".
 *
 * As constantes de contrato (BBA_*) são o que a Medicar exige em toda inclusão
 * de titular; vêm da consulta de contrato deles e são fixas por ambiente.
 */
export function SettingsIntegrationsMedicarPage() {
  const { can } = useAuthority()
  // PUT /settings/integrations/medicar e DELETE .../password usam a mesma permissão.
  const canManage = can('settings.integrations_medicar.manage')
  const [data, setData] = useState<MedicarSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [endpoint, setEndpoint] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')       // vazio = manter a gravada
  const [tenantId, setTenantId] = useState('')
  const [cnpjMedicar, setCnpjMedicar] = useState('')
  const [codInt, setCodInt] = useState('')
  const [codEmp, setCodEmp] = useState('')
  const [conEmp, setConEmp] = useState('')
  const [verCon, setVerCon] = useState('')
  const [subCon, setSubCon] = useState('')
  const [verSub, setVerSub] = useState('')
  const [codPro, setCodPro] = useState('')
  const [versao, setVersao] = useState('')
  const [grupoEmpresa, setGrupoEmpresa] = useState('')
  const [contrato, setContrato] = useState('')
  const [blockReason, setBlockReason] = useState('')
  const [addressPostalCode, setAddressPostalCode] = useState('')
  const [addressStreet, setAddressStreet] = useState('')
  const [addressNumber, setAddressNumber] = useState('')
  const [addressComplement, setAddressComplement] = useState('')
  const [addressDistrict, setAddressDistrict] = useState('')
  const [addressCity, setAddressCity] = useState('')
  const [addressState, setAddressState] = useState('')
  const [cepLoading, setCepLoading] = useState(false)
  // Ritmo: null enquanto não carrega, e null no campo vazio (volta ao padrão).
  const [reconcileLimit, setReconcileLimit] = useState<number | null>(null)
  const [reconcileInterval, setReconcileInterval] = useState<number | null>(null)
  const [syncMax, setSyncMax] = useState<number | null>(null)

  function hydrate(s: MedicarSettings) {
    setData(s)
    setEndpoint(s.endpoint ?? ''); setUsername(s.username ?? ''); setPassword('')
    setTenantId(s.tenantId ?? ''); setCnpjMedicar(s.cnpjMedicar ?? '')
    setCodInt(s.codInt ?? ''); setCodEmp(s.codEmp ?? ''); setConEmp(s.conEmp ?? ''); setVerCon(s.verCon ?? '')
    setSubCon(s.subCon ?? ''); setVerSub(s.verSub ?? ''); setCodPro(s.codPro ?? ''); setVersao(s.versao ?? '')
    setGrupoEmpresa(s.grupoEmpresa ?? ''); setContrato(s.contrato ?? ''); setBlockReason(s.blockReason ?? '')
    setAddressPostalCode(maskCep(s.addressPostalCode ?? '')); setAddressStreet(s.addressStreet ?? '')
    setAddressNumber(s.addressNumber ?? ''); setAddressComplement(s.addressComplement ?? '')
    setAddressDistrict(s.addressDistrict ?? ''); setAddressCity(s.addressCity ?? ''); setAddressState(s.addressState ?? '')
    setReconcileLimit(s.reconcileLimit); setReconcileInterval(s.reconcileIntervalHours); setSyncMax(s.syncMaxRequests)
  }

  useEffect(() => {
    Service.get()
      .then(hydrate)
      .catch((e) => toast.error({ title: 'Não foi possível carregar', description: extractApiError(e).message }))
      .finally(() => setLoading(false))
  }, [])

  async function save() {
    setSaving(true)
    try {
      hydrate(await Service.update({
        endpoint: endpoint.trim() || null, username: username.trim() || null,
        password: password.trim() || null, tenantId: tenantId.trim() || null,
        cnpjMedicar: cnpjMedicar.trim() || null, codInt: codInt.trim() || null, codEmp: codEmp.trim() || null,
        conEmp: conEmp.trim() || null, verCon: verCon.trim() || null, subCon: subCon.trim() || null,
        verSub: verSub.trim() || null, codPro: codPro.trim() || null, versao: versao.trim() || null,
        grupoEmpresa: grupoEmpresa.trim() || null, contrato: contrato.trim() || null, blockReason: blockReason.trim() || null,
        addressPostalCode: onlyDigits(addressPostalCode) || null, addressStreet: addressStreet.trim() || null,
        addressNumber: addressNumber.trim() || null, addressComplement: addressComplement.trim() || null,
        addressDistrict: addressDistrict.trim() || null, addressCity: addressCity.trim() || null,
        addressState: addressState.trim().toUpperCase() || null,
        reconcileLimit, reconcileIntervalHours: reconcileInterval, syncMaxRequests: syncMax,
      }))
      toast.success({ title: 'Integração salva', description: 'Use "Testar conexão" em Integrações → Medicar para conferir.' })
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  async function clearPassword() {
    try {
      hydrate(await Service.clearPassword())
      toast.success({ title: 'Senha removida', description: 'A sincronização não roda até uma nova senha ser gravada.' })
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  const pw = data?.password

  async function lookupCep() {
    const cleaned = onlyDigits(addressPostalCode)
    if (cleaned.length !== 8) { toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' }); return }
    setCepLoading(true)
    try {
      const data = await SalesLinkService.lookupCep(cleaned)
      if (!data) { toast.error({ title: 'CEP não encontrado', description: 'Verifique o CEP digitado.' }); return }
      setAddressStreet(data.address); setAddressDistrict(data.district)
      setAddressCity(data.cityName); setAddressState(data.stateAbbr)
    } catch {
      toast.error({ title: 'Falha ao buscar CEP', description: 'Verifique sua conexão e tente novamente.' })
    } finally { setCepLoading(false) }
  }

  const sections: FormSection[] = [
    {
      key: 'situacao', label: 'Situação', icon: Plug,
      content: (
        <div className="rounded-md bg-white p-5 ring-1 ring-gray-200 sm:p-6">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">Situação da integração</span>
            <Badge variant={data?.complete ? 'green' : 'amber'}>{data?.complete ? 'configurada' : 'incompleta'}</Badge>
          </div>
          <p className="text-xs text-gray-500">{data?.statusText}</p>

          {(data?.missing.length ?? 0) > 0 && (
            <div className="mt-3 rounded-md bg-amber-50 px-3 py-2.5 text-xs text-amber-800 ring-1 ring-amber-200">
              <p className="mb-1 font-medium">Falta preencher:</p>
              <ul className="list-disc pl-4">{data!.missing.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}

          <div className={`mt-3 rounded-md px-3 py-2.5 text-xs ring-1 ${data?.linkedPlans ? 'bg-gray-50 text-gray-700 ring-gray-200' : 'bg-rose-50 text-rose-800 ring-rose-200'}`}>
            {data?.linkedPlans
              ? <><strong>{data.linkedPlans}</strong> plano(s) têm a Medicar no produto — são os associados desses planos que entram na sincronização.</>
              : <><strong>Nenhum plano tem a Medicar no produto.</strong> Enquanto isso a sincronização não envia ninguém. O vínculo é feito em <Link to="/settings/commercial/products" className="underline">Configurações → Produtos</Link>, adicionando um serviço prestado pela Medicar à composição do produto.</>}
          </div>
        </div>
      ),
    },
    {
      key: 'acesso', label: 'Acesso', icon: KeyRound,
      content: (
        <Section icon={<KeyRound size={15} />} title="Acesso à API"
          description="Endereço, usuário e senha fornecidos pela Medicar. A senha fica criptografada e nunca volta para a tela.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Endereço da API" required wrapperClassName="md:col-span-2" value={endpoint}
              onChange={e => setEndpoint(e.target.value)} placeholder="https://host:porta/rest"
              hint={data?.fromEnv.endpoint ? `Vazio: vale o do servidor (${data.fromEnv.endpoint})` : 'Termina em /rest — sem barra no final.'} />
            <Input label="Usuário" required value={username} onChange={e => setUsername(e.target.value)}
              placeholder="Usuário da API" autoComplete="off"
              hint={data?.fromEnv.username ? `Vazio: vale o do servidor (${data.fromEnv.username})` : undefined} />
            <div>
              <div className="flex flex-wrap items-end gap-2">
                <Input label="Senha" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder={pw?.filled ? 'Preenchida — digite para substituir' : 'Senha da API'}
                  wrapperClassName="flex-1" autoComplete="new-password" />
                {pw?.filled && canManage && (
                  <Button variant="ghost" size="sm" onClick={clearPassword} iconLeft={<Trash2 size={13} />}>Limpar</Button>
                )}
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                {pw?.filled && <Badge variant="green">salva {pw.hint}</Badge>}
                {!pw?.filled && pw?.fromEnv && <Badge variant="gray">vindo do .env do servidor</Badge>}
                {!pw?.filled && !pw?.fromEnv && <Badge variant="amber">não configurada</Badge>}
                Em branco mantém a senha já gravada.
              </p>
            </div>
            <Input label="Tenant ID" required value={tenantId} onChange={e => setTenantId(e.target.value)}
              placeholder="01,006001" hint="Cabeçalho tenantid — vem da consulta de contrato da Medicar." />
          </div>
        </Section>
      ),
    },
    {
      key: 'contrato', label: 'Contrato', icon: FileSignature,
      content: (
        <Section icon={<FileSignature size={15} />} title="Dados do contrato"
          description="Códigos que a Medicar exige em toda inclusão de titular (MASTERBBA). São fixos por ambiente e vêm da consulta de contrato deles.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Operadora (BBA_CODINT)" required value={codInt} onChange={e => setCodInt(e.target.value)} placeholder="1001" />
            <Input label="Empresa (BBA_CODEMP)" required value={codEmp} onChange={e => setCodEmp(e.target.value)} placeholder="0004" />
            <Input label="Plano (BBA_CODPRO)" required value={codPro} onChange={e => setCodPro(e.target.value)} placeholder="0066" />
            <Input label="Versão (BBA_VERSAO)" required value={versao} onChange={e => setVersao(e.target.value)} placeholder="001" />
            <Input label="Contrato (BBA_CONEMP)" value={conEmp} onChange={e => setConEmp(e.target.value)} placeholder="Só pessoa jurídica" />
            <Input label="Versão do contrato" value={verCon} onChange={e => setVerCon(e.target.value)} placeholder="001" />
            <Input label="Subcontrato (BBA_SUBCON)" value={subCon} onChange={e => setSubCon(e.target.value)} placeholder="002329145" />
            <Input label="Versão do subcontrato" value={verSub} onChange={e => setVerSub(e.target.value)} placeholder="001" />
            <Input label="CNPJ da filial Medicar" required value={cnpjMedicar} onChange={e => setCnpjMedicar(e.target.value)}
              placeholder="00.000.000/0000-00" hint="Query cnpjmedicar da consulta de contrato." />
            <Input label="Grupo empresa" required value={grupoEmpresa} onChange={e => setGrupoEmpresa(e.target.value)} placeholder="000" hint="Query grupoempresa." />
            <Input label="Número do contrato" required value={contrato} onChange={e => setContrato(e.target.value)}
              placeholder={subCon || '000000000'}
              hint="Query contrato da consulta de matrícula. Pela documentação da Medicar, é o mesmo número do subcontrato (BBA_SUBCON)." />
          </div>
          {data?.contractWarning && (
            <div className="mt-3 rounded-md bg-amber-50 px-3 py-2.5 text-xs text-amber-900 ring-1 ring-amber-200">
              <strong>Consulta de matrícula com o contrato errado.</strong> {data.contractWarning}
              {' '}Enquanto isso não for corrigido, ninguém sai de “aguardando análise”, mesmo depois de aprovado na Medicar.
            </div>
          )}
        </Section>
      ),
    },
    {
      key: 'endereco', label: 'Endereço de envio', icon: MapPin,
      content: (
        <Section icon={<MapPin size={15} />} title="Endereço de envio"
          description="Vai no cadastro de todo beneficiário enviado. O Protheus da Medicar valida o CEP contra a tabela deles e recusa CEP que não exista lá — por isso a orientação é enviar o endereço da Ampla+, e não o do associado. Digite o CEP e clique na lupa para preencher automaticamente.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="CEP" required value={addressPostalCode} onChange={e => setAddressPostalCode(maskCep(e.target.value))}
              placeholder="00000-000" hint="Obrigatório: sem ele a inclusão volta com erro de CEP (alias BC9)."
              actionRight={(
                <button type="button" onClick={lookupCep} disabled={cepLoading} aria-label="Buscar CEP" title="Buscar CEP"
                  className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed">
                  {cepLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                </button>
              )} />
            <Input label="Logradouro" value={addressStreet} onChange={e => setAddressStreet(e.target.value)} placeholder="Rua, avenida" />
            <Input label="Número" value={addressNumber} onChange={e => setAddressNumber(e.target.value)} placeholder="123" />
            <Input label="Complemento" value={addressComplement} onChange={e => setAddressComplement(e.target.value)} placeholder="Sala, andar" />
            <Input label="Bairro" value={addressDistrict} onChange={e => setAddressDistrict(e.target.value)} placeholder="Bairro" />
            <Input label="Cidade" value={addressCity} onChange={e => setAddressCity(e.target.value)} placeholder="Cidade" />
            <Input label="UF" value={addressState} onChange={e => setAddressState(e.target.value.toUpperCase().slice(0, 2))} placeholder="SP" />
          </div>
        </Section>
      ),
    },
    {
      key: 'ritmo', label: 'Ritmo de envio', icon: Gauge,
      content: (
        <Section icon={<Gauge size={15} />} title="Ritmo de envio"
          description="A Medicar pede movimentação em vez de base inteira. O envio só manda quem entrou ou mudou; a conferência de quem já está lá roda sozinha, de hora em hora, e só consulta. Estes números seguram o volume — combine-os com eles se pedirem outro ritmo.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <NumberInput label="Consultas por conferência" value={reconcileLimit} onChange={setReconcileLimit}
              min={10} max={2000} placeholder="300"
              hint="Quantas pessoas a conferência de hora em hora pergunta por vez. Em branco volta a 300." />
            <NumberInput label="Intervalo entre conferências da mesma pessoa" value={reconcileInterval} onChange={setReconcileInterval}
              min={1} max={168} suffix=" h" placeholder="12"
              hint="Tempo mínimo antes de perguntar de novo pelo mesmo CPF. Em branco volta a 12 horas." />
            <NumberInput label="Envios por rodada" value={syncMax} onChange={setSyncMax}
              min={10} max={5000} placeholder="400" wrapperClassName="md:col-span-2"
              hint="Teto de inclusões, alterações e bloqueios em cada sincronização. O que passar disso sai na rodada seguinte. Em branco volta a 400." />
          </div>
        </Section>
      ),
    },
    {
      key: 'cancelamento', label: 'Cancelamento', icon: Activity,
      content: (
        <Section icon={<Activity size={15} />} title="Cancelamento"
          description="Quando um associado sai da regra (inativa ou perde o plano), a sincronização pede o bloqueio dele na Medicar com este motivo.">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Input label="Motivo de bloqueio (B9G_COD)" value={blockReason} onChange={e => setBlockReason(e.target.value)}
              placeholder="000001" hint="Código da tabela de motivos da Medicar. Vazio usa 000001." />
          </div>
        </Section>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Medicar (integração)"
        subtitle="Credenciais da API de beneficiários e dados do contrato"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Integrações' }, { label: 'Medicar' }]}
        backTo="/settings"
        actions={(
          <Button onClick={save} loading={saving} disabled={!canManage} iconLeft={<Save size={14} />}
            title={canManage ? undefined : 'Sem permissão para alterar a integração'}>Salvar</Button>
        )}
      />
      <FormSidemenu sections={sections} />
    </>
  )
}

function Section({ icon, title, description, children }: {
  icon: React.ReactNode; title: string; description: string; children: React.ReactNode
}) {
  return (
    <div className="rounded-md bg-white p-5 ring-1 ring-gray-200 sm:p-6">
      <h2 className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
        <span className="text-brand-600">{icon}</span>{title}
      </h2>
      <p className="mb-4 text-xs text-gray-500">{description}</p>
      {children}
    </div>
  )
}
